import { ModalProvider } from '@react-aria/overlays';
import { fn } from 'storybook/test';

import type { Meta, StoryFn } from '@storybook/react';
import type { ModalDialogProps } from './ModalDialog';

import { ModalDialog } from './ModalDialog';

import { Button } from 'components/Buttons';
import { ButtonGroup } from 'components/ButtonGroup';
import { FormField } from 'components/FormField';
import { InputField } from 'components/InputField';
import { Label } from 'components/Label';

const codeString = `
/* ModalDialog has more styling props (see Info-tab).
 Code example is made with the most commonly used props. **/
  <ModalDialog isOpen={isOpen} title={title} onClose={onCloseFunction}
    onSubmit={onSubmitFunction} buttons={buttons}> {children} </ModalDialog>
`;

// eslint-disable-next-line import/no-default-export
export default {
  title: 'Molecules/ModalDialog',
  component: ModalDialog,
  argTypes: {
    isOpen: {
      table: {
        disable: true,
      },
    },
    noValidate: {
      control: 'boolean',
    },
    role: {
      control: 'radio',
      options: ['dialog', 'alertdialog'],
    },
    onSubmit: {
      table: {
        disable: true,
      },
    },
    onClose: {
      table: {
        disable: true,
      },
    },
    buttons: {
      table: {
        disable: true,
      },
    },
  },
  parameters: {
    inspectComponent: ModalDialog,
    codeString: codeString,
  },
} as Meta<ModalDialogProps>;

const Template: StoryFn<ModalDialogProps> = (args: ModalDialogProps) => (
  <ModalProvider>
    <ModalDialog {...args}>
      <FormField outerSpace>
        <Label htmlFor="text-field">Example Text Field</Label>
        <InputField id="text-field" />
      </FormField>
    </ModalDialog>
  </ModalProvider>
);

const defaultArgs: Partial<ModalDialogProps> = {
  padded: false,
  size: 'regular',
  noButtonBorder: false,
  variant: 'dialog',
  role: 'dialog',
  title: 'Example Title',
  closeButtonAriaValueText: 'Close',
  noValidate: false,
  onSubmit: fn(),
  onClose: fn(),
  buttons: (
    <ButtonGroup reversed>
      <Button variant="primary" onClick={fn()}>
        OK
      </Button>
      <Button variant="secondary" onClick={fn()}>
        Cancel
      </Button>
    </ButtonGroup>
  ),
  isOpen: true,
};

export const ModalDialogExample = Template.bind({});
ModalDialogExample.args = {
  ...defaultArgs,
};

export const Loading = Template.bind({});
Loading.args = {
  ...defaultArgs,
  isLoading: true,
  spinnerAriaValueText: 'Loading',
};

const LongContentTemplate: StoryFn<ModalDialogProps> = (args: ModalDialogProps) => (
  <ModalProvider>
    <ModalDialog {...args}>
      <FormField outerSpace>
        <div>
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut ac dui et nibh eleifend ultricies a id nulla.
          Pellentesque eleifend sem a tellus dictum tempus. Proin non leo id risus placerat vulputate at ac erat. Ut
          fringilla imperdiet sollicitudin. Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere
          cubilia curae; Cras molestie ante ac magna ornare rhoncus non at quam. Mauris elementum luctus euismod.
          Pellentesque molestie malesuada purus, sed egestas lacus interdum sit amet. Morbi blandit lacus sit amet
          dictum pulvinar. Ut semper nunc ac enim imperdiet, non interdum diam gravida. In nunc tortor, mollis ac
          molestie dignissim, elementum sed quam. Suspendisse mollis venenatis libero, pellentesque vestibulum elit
          consequat et. Aliquam fringilla tellus id libero tempus, sit amet suscipit augue tristique. Quisque cursus
          neque vel justo tristique, eget semper turpis elementum. Etiam venenatis mattis sapien ac mattis. Mauris vitae
          ultrices erat.
        </div>
        <div>
          Aliquam facilisis massa sed justo rhoncus bibendum. Sed lobortis urna orci, vitae porta leo placerat nec. Nam
          porta porttitor ornare. Suspendisse vehicula nunc nec egestas finibus. Aliquam non luctus risus. Phasellus ac
          diam tincidunt, bibendum sem eget, sollicitudin neque. Curabitur mauris risus, feugiat ac euismod non,
          vulputate ac justo. Curabitur pretium vehicula augue, blandit porta odio rutrum sit amet. Proin semper enim ut
          sapien tempor, a ornare ipsum faucibus.
        </div>
      </FormField>
    </ModalDialog>
  </ModalProvider>
);

export const WithLongContentAndNoBorder = LongContentTemplate.bind({});
WithLongContentAndNoBorder.args = {
  ...defaultArgs,
  noButtonBorder: true,
};
